import React, { useEffect } from 'react';
import { getOpenHoursTextForUniteus } from '@src/utills/utils';
import { capitalizeSentenceFirstLetters } from '@repo/utils';
import { renderToString } from 'react-dom/server';
import { AbyssProvider } from '@uhg-abyss/web/ui/AbyssProvider';
import { createTheme } from '@uhg-abyss/web/tools/theme';
import {
  UniteUsOfficeLocations,
  UniteUsProgram,
} from '../../../../../packages/ui/src';
import { CboCardUniteUsNextSteps } from '@repo/ui';

type EmailBodyUniteUs = {
  program: UniteUsProgram;
  updateEmailBody: (emailObjuniteus: any) => void;
};
export const EmailBodyUniteUs = ({
  program,
  updateEmailBody,
}: EmailBodyUniteUs) => {
  function capitlalizeWordFirstLetter(word: string): string {
    return word?.charAt(0)?.toUpperCase() + word?.slice(1);
  }
  const cost =
    program?.document?.payment_options &&
    program.document?.payment_options.length > 0
      ? program.document.payment_options
          .map((payment_option) =>
            {return capitlalizeWordFirstLetter(payment_option.replace(/_/g, ' '))}
          )
          .join(', ')
      : 'No pricing information is available';
  const closestLocation =
    program?.document?.locations.find((location) => {
      return location.is_closest;
    }) || program?.document?.locations[0];
  function getFullAddressUniteUsOffice(
    location: UniteUsOfficeLocations | undefined
  ) {
    if (location) {
      const { line_1, line_2, city, county, country } = location;
      return line_1 || line_2
        ? [line_1, line_2, city, county, country].filter(Boolean).join(', ')
        : '--';
    }
    return '--';
  }
  const websiteUrl = program?.document?.website_url;

  const addressText = getFullAddressUniteUsOffice(closestLocation);
  const email = closestLocation?.email_addresses?.[0] ?? '';

  const footer =
    'This resource was shared by United Healthcare Community Resources, powered by UniteUs.';

  const NextStepsElement = () => {
    return (
      <CboCardUniteUsNextSteps
        closestOfficeAddressText={addressText}
        closestOfficeEmail={email}
        closestOfficePhone={closestLocation?.phone_numbers ?? ['']}
        closestOfficeWebsite={websiteUrl ?? ''}
      />
    );
  };
  function getMainServices(): string {
    if (
      program?.relationships &&
      program?.relationships?.services &&
      Array.isArray(program?.relationships?.services.data)
    ) {
      return program.relationships.services.data
        .map((service) => {
          return service.label
            ? capitalizeSentenceFirstLetters(service.label)
            : '';
        })
        .filter(Boolean)
        .join(', ');
    }
    return 'No main services information available';
  }

  function getCoverageText(): string {
    return (
      program?.document?.locations[0]?.county ??
      'No coverage area information is available'
    );
  }

  function getAttributeTags(): string {
    const populationSpecializations =
      program?.document?.population_specializations;

    return populationSpecializations && populationSpecializations.length > 0
      ? populationSpecializations
          .map((tag) => {
            return capitalizeSentenceFirstLetters(tag.replace(/_/g, ' '));
          })
          .join(', ')
      : 'No serving information available';
  }

  function getEligibility(): string {
    const eligibilityText =
      program?.document?.eligibility_text ??
      'No eligibility information is available';
    return eligibilityText.replace(/<\/?[^>]+(>|$)/g, '');
  }

  function getAvailability(): string {
    return program?.document?.is_active && program?.document?.receiving_referrals
      ? 'Available'
      : 'No availability information is available';
  }
  const displayNames = new Intl.DisplayNames(['en'], { type: 'language' });

  function getLanguages(): string {
    return program?.document?.languages.length > 0
      ? program?.document?.languages
          .map((language) => {
            return displayNames.of(language);
          })
          .join(', ')
      : 'No language information is available';
  }

  const emailObjuniteus = {
    name: program?.document.name,
    description: program?.document?.description||'',
    provider: program?.relationships?.provider?.data?.document?.name||'',
    mainServices: getMainServices(),
    serving: getAttributeTags(),
    eligibility: getEligibility(),
    cost,
    languages: getLanguages(),
    availability: getAvailability(),
    coverageArea: getCoverageText(),
    location: addressText,
    distance: program?.document?.distance
      ? parseFloat(program?.document?.distance.replace('mi', '').trim())
      : 0,
    hours: {
      sunday: getOpenHoursTextForUniteus(
        program?.document?.locations[0],
        'sunday'
      ),
      monday: getOpenHoursTextForUniteus(
        program?.document?.locations[0],
        'monday'
      ),
      tuesday: getOpenHoursTextForUniteus(
        program?.document?.locations[0],
        'tuesday'
      ),
      wednesday: getOpenHoursTextForUniteus(
        program?.document?.locations[0],
        'wednesday'
      ),
      thursday: getOpenHoursTextForUniteus(
        program?.document?.locations[0],
        'thursday'
      ),
      friday: getOpenHoursTextForUniteus(
        program?.document?.locations[0],
        'friday'
      ),
      saturday: getOpenHoursTextForUniteus(
        program?.document?.locations[0],
        'saturday'
      ),
    },
    nextStep: renderToString(<AbyssProvider theme={createTheme('uhc')}><NextStepsElement /></AbyssProvider>),
    footer,
  };

  useEffect(() => {
    updateEmailBody(emailObjuniteus);
  }, [emailObjuniteus]);

  return '';
};
